import type { RouteSurface } from "~/api/opsTypes";

/**
 * Which surface the router is on, and whether the entity id is anything the server can use.
 *
 * A five-way prefix match, evaluated top to bottom — the same order kms used, over slate's own
 * paths. The `tasks` branch exists for completeness and is unreachable from the bar: Task
 * Center has its own inline composer, and a floating bar there would be a second one.
 */
export function surfaceFor(pathname: string): RouteSurface {
  if (pathname.startsWith("/settings/flows")) {
    return "flows";
  }
  if (pathname.startsWith("/settings/task-center")) {
    return "tasks";
  }
  if (pathname.startsWith("/documents/")) {
    return "document";
  }
  if (pathname.startsWith("/collections/")) {
    return "collection";
  }
  if (pathname.startsWith("/search")) {
    return "search";
  }
  return "app";
}

/** The bar is hidden where a better composer already exists. */
export function askBarHidden(pathname: string): boolean {
  return pathname.startsWith("/settings/task-center");
}

/**
 * `routeId` only where the surface can use it.
 *
 * The server reads it on the `document`/`collection` branches and ignores it elsewhere; kms
 * sent the last-visited document id on every screen (§E6.2), which made every request look
 * like it was about a document the person had left.
 *
 * The id is the shell's `activeEntityId` when a scene has set one (the document scene does),
 * else the second path segment — `/collections/<id>` sets no entity id, and without this the
 * collection surface sent NO `routeId`, so the server could not scope "the doc about X" to the
 * collection the person is looking at (S5).
 */
export function routeIdFor(
  surface: RouteSurface,
  activeEntityId: string | null,
  pathname = "",
): string | undefined {
  if (surface !== "document" && surface !== "collection") {
    return undefined;
  }
  return activeEntityId ?? entityIdFromPath(pathname) ?? undefined;
}

/** `/collections/<id>` or `/documents/<id>` → `<id>`; anything else → null. */
function entityIdFromPath(pathname: string): string | null {
  const match = /^\/(?:collections|documents)\/([^/?#]+)/.exec(pathname);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * The route of a document slate just CREATED. A navigate target is never built from this: the
 * server's `path` says where a target lives (S3), because only it knows which kind the id names.
 */
export function documentPath(documentId: string): string {
  return `/documents/${documentId}`;
}
